import React, { useState, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import moment from 'moment'
import Select from 'react-select'
import { CalendarItem } from '../components/calendar/CalendarItem'
import { useGeneralTimelocks } from '../hooks/useGeneralTimelocks'

const getFirstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)
const getLastDayOfMonth  = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0)

const getDaysInMonth = (date: Date): Date[] => {
  const days: Date[] = []
  const first = getFirstDayOfMonth(date)
  const last  = getLastDayOfMonth(date)

  for (let i = first.getDate(); i <= last.getDate(); i++) {
    days.push(new Date(date.getFullYear(), date.getMonth(), i))
  }
  const firstDayIndex = first.getDay()
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    days.unshift(new Date(first.getFullYear(), first.getMonth(), -i))
  }
  const lastDayIndex = last.getDay()
  if (lastDayIndex !== 6) {
    for (let i = 1; i <= 6 - lastDayIndex; i++) {
      days.push(new Date(last.getFullYear(), last.getMonth(), last.getDate() + i))
    }
  }
  return days
}

const isToday = (date: Date) => {
  const today = new Date()
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
}

const isCurrentMonth = (date: Date, ref: Date) =>
  date.getMonth() === ref.getMonth() && date.getFullYear() === ref.getFullYear()

export default function TimelockCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [dateRange, setDateRange] = useState<Date[]>([])
  const [selectedEnvironment, setSelectedEnvironment] = useState<string | null>(null)
  const { data, isLoading } = useGeneralTimelocks([])

  useEffect(() => {
    setDateRange(getDaysInMonth(currentDate))
  }, [currentDate])

  const environments = (): { value: string | null; label: string }[] => {
    if (!data) return []
    const unique = [...new Set(data.map((t: any) => t.environment))].filter(Boolean) as string[]
    return [{ value: null, label: 'All environments' }, ...unique.map((e) => ({ value: e, label: e }))]
  }

  const moveMonth = (n: number) => {
    const d = new Date(currentDate)
    d.setMonth(currentDate.getMonth() + n)
    setCurrentDate(d)
  }

  const timelocksByDate = (date: Date): any[] => {
    if (!data) return []
    const target = moment(date)
    return (data as any[])
      .filter((t) => {
        const allowed = moment.unix(Number(t.timelock?.allowedAt))
        const sameDay = target.isSame(allowed, 'day')
        const matchEnv = selectedEnvironment ? t.environment === selectedEnvironment : true
        return sameDay && matchEnv
      })
      .sort((a, b) => Number(a.timelock?.allowedAt) - Number(b.timelock?.allowedAt))
  }

  if (isLoading) return <LoaderBar />

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <Wrap>
      <PageHeader>
        <div>
          <PageTitle>Calendar</PageTitle>
          <PageSubtitle>
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </PageSubtitle>
        </div>
        <Controls>
          <SelectWrap>
            <StyledSelect
              classNamePrefix="Select"
              options={environments()}
              onChange={(opt: any) => setSelectedEnvironment(opt?.value ?? null)}
              isClearable
              placeholder="All environments"
            />
          </SelectWrap>
          <NavBtn onClick={() => moveMonth(-1)}>← Prev</NavBtn>
          <NavBtn onClick={() => moveMonth(1)}>Next →</NavBtn>
        </Controls>
      </PageHeader>

      <WeekRow>
        {daysOfWeek.map((d) => <WeekDay key={d}>{d}</WeekDay>)}
      </WeekRow>

      <Grid>
        {dateRange.map((date, i) => {
          const inMonth = isCurrentMonth(date, currentDate)
          const today   = isToday(date)
          const events  = timelocksByDate(date)

          return (
            <Cell key={i} $dimmed={!inMonth} $today={today}>
              <DayNum $today={today}>{date.getDate()}</DayNum>
              {events.length > 0 && (
                <Events>
                  {events.map((t, j) => (
                    <CalendarItem key={j} timelock={t}>{t.environment}</CalendarItem>
                  ))}
                </Events>
              )}
            </Cell>
          )
        })}
      </Grid>
    </Wrap>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const shimmer = keyframes`0%{transform:translateX(-100%)}100%{transform:translateX(100%)}`
const LoaderBar = styled.div`
  height: 2px; background: rgba(255,255,255,0.06); border-radius: 2px; overflow: hidden; position: relative;
  &::after { content:''; display:block; position:absolute; inset:0;
    background: linear-gradient(90deg,transparent,rgba(111,92,230,0.5),transparent);
    animation: ${shimmer} 1.4s ease infinite; }
`

const Wrap = styled.div`display: flex; flex-direction: column; gap: 16px;`

const PageHeader = styled.div`
  display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap;
`

const PageTitle = styled.h1`
  font-size: 20px; font-weight: 600; color: #ededed; margin: 0 0 2px; letter-spacing: -0.02em;
`

const PageSubtitle = styled.p`font-size: 12px; color: #a1a1a1; margin: 0;`

const Controls = styled.div`display: flex; align-items: center; gap: 8px;`

const SelectWrap = styled.div`width: 200px;`

const NavBtn = styled.button`
  height: 28px; padding: 0 10px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 5px; color: #777; font-size: 11px; cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  &:hover { border-color: rgba(255,255,255,0.15); color: #a0a0a0; }
`

const WeekRow = styled.div`
  display: grid; grid-template-columns: repeat(7, 1fr);
  border-bottom: 1px solid rgba(255,255,255,0.05); padding-bottom: 8px;
`

const WeekDay = styled.div`
  text-align: center; font-size: 10px; font-weight: 600; color: #777;
  text-transform: uppercase; letter-spacing: 0.06em;
`

const Grid = styled.div`display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px;`

const Cell = styled.div<{ $dimmed: boolean; $today: boolean }>`
  min-height: 110px;
  padding: 8px 6px 6px;
  border-radius: 6px;
  border: 1px solid ${({ $today }) => $today ? 'rgba(111,92,230,0.4)' : 'rgba(255,255,255,0.06)'};
  background: ${({ $today, $dimmed }) => $today ? 'rgba(111,92,230,0.07)' : $dimmed ? 'transparent' : 'rgba(255,255,255,0.02)'};
  opacity: ${({ $dimmed }) => $dimmed ? 0.35 : 1};
  display: flex; flex-direction: column; gap: 4px;
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: rgba(255,255,255,0.12); background: rgba(255,255,255,0.04); }
`

const DayNum = styled.div<{ $today: boolean }>`
  font-size: 11px; font-weight: 600; line-height: 1; margin-bottom: 2px;
  color: ${({ $today }) => $today ? '#a89aff' : '#666'};
`

const Events = styled.div`
  display: flex; flex-direction: column; gap: 2px;
  overflow-y: auto; max-height: 80px;
  &::-webkit-scrollbar { width: 2px; }
  &::-webkit-scrollbar-thumb { background: rgba(111,92,230,0.3); border-radius: 2px; }
`

const StyledSelect = styled(Select)`
  .Select__control {
    background: #0d0d0d; border: 1px solid rgba(255,255,255,0.08); border-radius: 5px;
    min-height: 28px; height: 28px; box-shadow: none; cursor: pointer;
    &:hover { border-color: rgba(255,255,255,0.15); }
    &.Select__control--is-focused { border-color: rgba(111,92,230,0.4); }
  }
  .Select__value-container { padding: 0 8px; }
  .Select__placeholder { color: #777; font-size: 12px; }
  .Select__single-value { color: #a0a0a0; font-size: 12px; }
  .Select__input-container { color: #ededed; font-size: 12px; margin: 0; padding: 0; }
  .Select__indicator-separator { background: rgba(255,255,255,0.07); }
  .Select__dropdown-indicator { color: #777; padding: 0 6px; &:hover { color: #a1a1a1; } }
  .Select__clear-indicator { color: #777; padding: 0 4px; cursor: pointer; &:hover { color: #a1a1a1; } }
  .Select__menu {
    background: #161616; border: 1px solid rgba(255,255,255,0.07); border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.5); z-index: 100;
  }
  .Select__option {
    background: transparent; color: #a0a0a0; font-size: 12px; cursor: pointer;
    &:hover, &.Select__option--is-focused { background: rgba(255,255,255,0.04); color: #ededed; }
    &.Select__option--is-selected { background: rgba(111,92,230,0.15); color: #a89aff; }
  }
`
